import ReportFormatter from '@/services/report_formatter'

function resolveSeriesData (indicator, viewMode) {
  const data = indicator.data || {}

  if (viewMode === 'nominal' || viewMode === '' || viewMode === null || viewMode === undefined) {
    return data
  }

  if (viewMode === 'deflated' && data.adjustments && data.adjustments.deflated) {
    return data.adjustments.deflated
  }

  if (viewMode === 'withoutKnownParkOutliers' && data.adjustments && data.adjustments.withoutKnownParkOutliers) {
    return data.adjustments.withoutKnownParkOutliers
  }

  return data
}

function resolveTableData (indicator, viewMode) {
  const data = indicator.data || {}

  if (viewMode === 'deflated' && data.adjustments && data.adjustments.deflated) {
    return data.adjustments.deflated
  }

  return data
}

function isPercentageDistribution (data) {
  if (!data || !data.columns || !data.rows || data.rows.length === 0) {
    return false
  }

  const valueColumn = data.columns[data.columns.length - 1]
  const valueColumnLabel = String(valueColumn || '').toLowerCase()

  return valueColumnLabel.indexOf('%') !== -1 || valueColumnLabel.indexOf('percent') !== -1
}

function tableDataToChartResult (data) {
  const result = {}

  ;(data.rows || []).forEach(function (row) {
    const cells = row.cells || row
    if (!cells || cells.length < 2) {
      return
    }

    const label = extractCellValue(cells[0])
    const value = normalizeChartValue(extractCellValue(cells[cells.length - 1]))
    if (label !== undefined && label !== null && label !== '' && value !== null) {
      result[label] = value
    }
  })

  return result
}

function snapshotStageResult (snapshot) {
  if (snapshot.planejamento === undefined && snapshot.implantacao === undefined && snapshot.operacao === undefined) {
    return null
  }

  return {
    Planejamento: snapshot.planejamento || 0,
    Implantação: snapshot.implantacao || 0,
    Operação: snapshot.operacao || 0
  }
}

function normalizeChartValue (value) {
  if (value === null || value === undefined || value === '') {
    return null
  }

  if (typeof value === 'object' && value !== null && value.value !== undefined) {
    return normalizeChartValue(value.value)
  }

  const num = Number(value)
  return Number.isFinite(num) ? num : null
}

function extractCellValue (cell) {
  if (cell === null || cell === undefined) {
    return null
  }
  if (typeof cell === 'object' && cell.value !== undefined) {
    return cell.value
  }
  return cell
}

function seriesToChartPayload (seriesData) {
  return {
    labels: seriesData.labels || [],
    datasets: (seriesData.datasets || []).map(function (dataset) {
      return {
        label: dataset.label,
        result: dataset.values || {}
      }
    })
  }
}

function isPercentageSeries (indicator, seriesData) {
  const unit = indicator.metadata && indicator.metadata.unit
  if (unit === 'BRL') {
    return false
  }

  const datasets = (seriesData && seriesData.datasets) || (indicator.data && indicator.data.datasets) || []
  if (datasets.length === 0) {
    return false
  }

  const values = Object.values(datasets[0].values || {})
    .map(normalizeChartValue)
    .filter(function (value) { return value !== null })

  if (values.length === 0) {
    return false
  }

  const max = Math.max.apply(null, values)
  return max <= 100
}

function countDistributionCategories (result) {
  return Object.keys(result || {}).length
}

function buildDistributionChart (chartPayload, title) {
  const categoryCount = countDistributionCategories(chartPayload[0] && chartPayload[0].result)
  if (categoryCount === 0) {
    return null
  }

  if (categoryCount <= 8) {
    const chart = ReportFormatter.percentageDoughnutChart(chartPayload, title)
    chart.layout = {
      type: 'doughnut',
      minHeight: 460,
      maintainAspectRatio: true
    }
    return chart
  }

  const chart = ReportFormatter.percentageHorizontalBarChart(chartPayload, title)
  chart.layout = {
    type: 'horizontalBar',
    minHeight: Math.max(320, categoryCount * 44 + 100),
    maintainAspectRatio: false
  }
  return chart
}

function shouldShowHistoricalSeriesChart (indicator) {
  if (indicator.type !== 'series') {
    return false
  }

  const data = indicator.data || {}
  const datasets = data.datasets || []
  if (datasets.length === 0) {
    return false
  }

  const snapshot = data.snapshot
  if (snapshot && isPercentageDistribution(snapshot) && datasets.length > 4) {
    return false
  }

  return true
}

function inferCellDataType (value, columnIndex, columns, metadata) {
  if (columnIndex === 0 || typeof value === 'string') {
    return 'text'
  }
  if (metadata && metadata.unit === 'BRL') {
    return 'currency'
  }
  const columnLabel = columns && columns[columnIndex] ? String(columns[columnIndex]) : ''
  if (columnLabel.indexOf('%') !== -1) {
    return 'percentage'
  }
  if (Number.isInteger(Number(value))) {
    return 'integer'
  }
  return 'text'
}

function formatRows (indicator, columns, rawRows) {
  return (rawRows || []).map(function (row) {
    const cells = row.cells || row

    const formattedRow = {
      cells: cells.map(function (value, index) {
        return {
          value: value,
          dataType: inferCellDataType(value, index, columns, indicator.metadata)
        }
      })
    }

    if (row.group !== undefined && row.group !== null && row.group !== '') {
      formattedRow.group = row.group
    }

    return formattedRow
  })
}

function tablePayloadFromData (indicator, data, title) {
  const columns = data.columns || []
  const rows = formatRows(indicator, columns, data.rows)

  return ReportFormatter.textual({
    columns: columns,
    rows: rows,
    metadata: indicator.metadata
  }, title)
}

export default {
  seriesToChart (indicator, viewMode) {
    const seriesData = resolveSeriesData(indicator, viewMode)
    const chartPayload = seriesToChartPayload(seriesData)
    const chart = ReportFormatter.chart(chartPayload, indicator.title)
    const datasetCount = (seriesData.datasets || []).length

    chart.layout = {
      type: 'line',
      minHeight: 420,
      maintainAspectRatio: false
    }

    if (datasetCount > 4) {
      chart.options.legend = {
        display: true,
        position: 'bottom',
        labels: {
          boxWidth: 12,
          fontSize: 11,
          padding: 8
        }
      }
    }

    if (isPercentageSeries(indicator, seriesData)) {
      chart.options.scales.yAxes[0].ticks.max = 100
      chart.options.tooltips.callbacks.label = function (tooltipItems) {
        const value = normalizeChartValue(tooltipItems.yLabel)
        if (value === null) {
          return 'Sem dado'
        }
        return value.toFixed(1).replace('.', ',') + '%'
      }
      chart.options.scales.yAxes[0].ticks.callback = function (value) {
        if (!Number.isFinite(Number(value))) {
          return ''
        }
        return Number(value).toFixed(0) + '%'
      }
    } else {
      chart.options.tooltips.callbacks.label = function (tooltipItems) {
        const value = normalizeChartValue(tooltipItems.yLabel)
        if (value === null) {
          return 'Sem dado'
        }
        return value.toLocaleString('pt-BR')
      }
    }

    return chart
  },

  seriesToBarChart (indicator, titleSuffix) {
    const snapshot = indicator.data && indicator.data.snapshot
    if (!snapshot || !snapshot.values) {
      return null
    }

    const chartPayload = [{
      label: indicator.title,
      result: snapshot.values
    }]

    const suffix = titleSuffix || snapshot.year || ''
    const title = suffix ? indicator.title + ' (' + suffix + ')' : indicator.title

    return ReportFormatter.percentageHorizontalBarChart(chartPayload, title)
  },

  snapshotToChart (indicator) {
    const snapshot = indicator.data && indicator.data.snapshot
    if (!snapshot) {
      return null
    }

    const yearLabel = snapshot.year ? String(snapshot.year) : ''

    if (isPercentageDistribution(snapshot)) {
      const chartPayload = [{
        label: indicator.title,
        result: tableDataToChartResult(snapshot)
      }]
      const title = yearLabel ? 'Distribuição em ' + yearLabel : 'Distribuição'
      return buildDistributionChart(chartPayload, title)
    }

    if (snapshot.values && Object.keys(snapshot.values).length > 0) {
      const normalizedValues = {}
      Object.keys(snapshot.values).forEach(function (key) {
        const value = normalizeChartValue(snapshot.values[key])
        if (value !== null) {
          normalizedValues[key] = value
        }
      })
      const chartPayload = [{ label: indicator.title, result: normalizedValues }]
      const title = yearLabel ? indicator.title + ' (' + yearLabel + ')' : indicator.title
      return buildDistributionChart(chartPayload, title)
    }

    const stageResult = snapshotStageResult(snapshot)
    if (stageResult) {
      const chartPayload = [{
        label: indicator.title,
        result: stageResult
      }]
      const title = yearLabel ? 'Parques por estágio em ' + yearLabel : 'Parques por estágio'
      const chart = ReportFormatter.chart(chartPayload, title)
      chart.layout = {
        type: 'bar',
        minHeight: 420,
        maintainAspectRatio: false
      }
      chart.options.layout = {
        padding: {
          top: 12,
          right: 16,
          bottom: 36,
          left: 16
        }
      }
      if (chart.options.scales && chart.options.scales.xAxes && chart.options.scales.xAxes[0]) {
        chart.options.scales.xAxes[0].ticks = Object.assign({}, chart.options.scales.xAxes[0].ticks || {}, {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          padding: 10
        })
      }
      return chart
    }

    return null
  },

  tableToDistributionChart (indicator, viewMode) {
    if (indicator.type !== 'table') {
      return null
    }

    const data = resolveTableData(indicator, viewMode || 'nominal')
    if (!isPercentageDistribution(data)) {
      return null
    }

    const chartPayload = [{
      label: indicator.title,
      result: tableDataToChartResult(data)
    }]

    return buildDistributionChart(chartPayload, indicator.title)
  },

  resolveTablePayload (indicator, viewMode) {
    const data = resolveTableData(indicator, viewMode || 'nominal')
    return tablePayloadFromData(indicator, data, indicator.title)
  },

  tableToReport (indicator, viewMode) {
    if (viewMode) {
      return this.resolveTablePayload(indicator, viewMode)
    }
    return tablePayloadFromData(indicator, indicator.data || {}, indicator.title)
  },

  rankingToReport (indicator) {
    return tablePayloadFromData(indicator, indicator.data || {}, indicator.title)
  },

  nestedTablesToReports (indicator) {
    const data = indicator.data || {}
    const reports = []

    if (data.byRegion && data.byRegion.rows && data.byRegion.rows.length > 0) {
      reports.push(tablePayloadFromData(
        indicator,
        data.byRegion,
        'Por região'
      ))
    }

    return reports
  },

  seriesDetailToReport (indicator) {
    const detail = indicator.data && indicator.data.detail
    if (!detail || typeof detail !== 'object') {
      return null
    }

    const years = Object.keys(detail).sort()
    const firstRow = detail[years[0]] || {}
    const columnKeys = Object.keys(firstRow)

    if (columnKeys.indexOf('desligadas') !== -1) {
      const rows = years.map(function (year) {
        const values = detail[year] || {}
        return {
          cells: [
            { value: year, dataType: 'text' },
            { value: values.desligadas, dataType: 'integer' },
            { value: values.acumulado, dataType: 'integer' },
            { value: values.vinculadas, dataType: 'integer' },
            { value: values.percentual, dataType: 'percentage' }
          ]
        }
      })

      return ReportFormatter.textual({
        columns: ['Ano', 'Desligadas no ano', 'Acumulado', 'Vinculadas', '% desligamento'],
        rows: rows,
        metadata: indicator.metadata
      }, 'Detalhamento por ano')
    }

    const columns = ['Ano'].concat(columnKeys)
    const rows = years.map(function (year) {
      const values = detail[year] || {}
      const cells = [{ value: year, dataType: 'text' }].concat(columnKeys.map(function (key) {
        const value = values[key]
        const dataType = typeof value === 'number' && !Number.isInteger(value) ? 'percentage' : 'integer'
        return { value: value, dataType: dataType }
      }))
      return { cells: cells }
    })

    return ReportFormatter.textual({
      columns: columns,
      rows: rows,
      metadata: indicator.metadata
    }, 'Detalhamento por ano')
  },

  seriesVariationsToReport (indicator) {
    return this.comparisonToReport(indicator)
  },

  comparisonToReport (indicator) {
    const comparison = indicator.data && indicator.data.comparison
    if (!comparison) {
      return null
    }

    const compareYear = comparison.compareYear
    const referenceYear = comparison.referenceYear
    const title = 'Comparação ' + compareYear + ' vs ' + referenceYear + ' (%)'

    if (comparison.rows && comparison.rows.length > 0 && comparison.rows[0].label !== undefined) {
      const columns = ['Série', String(compareYear), String(referenceYear), 'Var. %']
      const rows = comparison.rows.map(function (row) {
        return {
          cells: [
            { value: row.label, dataType: 'text' },
            { value: row.compareValue, dataType: 'integer' },
            { value: row.referenceValue, dataType: 'integer' },
            {
              value: row.variationPercent === null || row.variationPercent === undefined ? '-' : row.variationPercent,
              dataType: row.variationPercent === null || row.variationPercent === undefined ? 'text' : 'percentage'
            }
          ]
        }
      })

      return ReportFormatter.textual({
        columns: columns,
        rows: rows,
        metadata: indicator.metadata
      }, title)
    }

    if (comparison.referenceValue !== undefined || comparison.compareValue !== undefined) {
      return ReportFormatter.textual({
        columns: ['Ano', 'Valor'],
        rows: [
          { cells: [{ value: compareYear, dataType: 'text' }, { value: comparison.compareValue, dataType: 'integer' }] },
          { cells: [{ value: referenceYear, dataType: 'text' }, { value: comparison.referenceValue, dataType: 'integer' }] },
          {
            cells: [
              { value: 'Var. %', dataType: 'text' },
              {
                value: comparison.variationPercent === null || comparison.variationPercent === undefined
                  ? '-'
                  : comparison.variationPercent,
                dataType: comparison.variationPercent === null || comparison.variationPercent === undefined
                  ? 'text'
                  : 'percentage'
              }
            ]
          }
        ],
        metadata: indicator.metadata
      }, title)
    }

    if (!comparison.columns || !comparison.rows || comparison.rows.length === 0) {
      return null
    }

    return tablePayloadFromData(indicator, comparison, title)
  },

  comparisonNestedTablesToReports (indicator) {
    const comparison = indicator.data && indicator.data.comparison
    if (!comparison || !comparison.byRegion || !comparison.byRegion.rows) {
      return []
    }

    return [
      tablePayloadFromData(
        indicator,
        comparison.byRegion,
        'Por região — comparação ' + comparison.compareYear + ' vs ' + comparison.referenceYear
      )
    ]
  },

  concentrationSummary (indicator) {
    const concentration = indicator.data && indicator.data.concentration
    if (!concentration || concentration.parksFor80Percent === undefined) {
      return null
    }

    return concentration.parksFor80Percent + ' de ' + concentration.totalParks +
      ' parques concentram 80% das empresas vinculadas'
  },

  hasFinancialAdjustments (indicator) {
    return !!(indicator.data && indicator.data.adjustments)
  },

  shouldShowHistoricalSeriesChart (indicator) {
    return shouldShowHistoricalSeriesChart(indicator)
  },

  distributionChartComponent (chartData) {
    if (!chartData || !chartData.layout) {
      return 'horizontalBar'
    }
    return chartData.layout.type || 'horizontalBar'
  },

  isPercentageDistributionTable (data) {
    return isPercentageDistribution(data)
  },

  formatKpiValue (indicator) {
    const value = indicator.data && indicator.data.value
    if (value === undefined || value === null) {
      return '-'
    }
    if (indicator.metadata && indicator.metadata.unit === 'BRL') {
      return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    }
    return Number(value).toLocaleString('pt-BR')
  }
}



// WEBPACK FOOTER //
// ./src/services/relatorio_estatistico_formatter.js